import AdminShell from "../components/admin/AdminShell";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Eye, Check, X, Clock, Monitor, Smartphone, HelpCircle, 
  ZoomIn, Settings, Phone, Mail, CalendarIcon, FileText, Activity, Search, Upload
} from "lucide-react";
import { normalizeText, containsNorm, useDebounce, paymentMethodLabel } from "../utils/search";

const API = '/api';

// Payment Table Component - defined outside to avoid re-creation on each render
const PaymentTable = ({ data, showActions = false, onViewVoucher, onReview, onAttachVoucher }) => (
  <div className="w-full max-w-full overflow-x-auto">
    <Table className="min-w-[720px]">
      <TableHeader>
        <TableRow className="border-white/15 hover:bg-white/10">
          <TableHead className="text-slate-400 min-w-[150px]">Usuario</TableHead>
          <TableHead className="text-slate-400">Ref.</TableHead>
          <TableHead className="text-slate-400 hidden lg:table-cell">Origen</TableHead>
          <TableHead className="text-slate-400 hidden sm:table-cell">Vehículo</TableHead>
          <TableHead className="text-slate-400">Monto</TableHead>
          <TableHead className="text-slate-400 hidden md:table-cell">Método</TableHead>
          <TableHead className="text-slate-400 hidden lg:table-cell">Fecha</TableHead>
          <TableHead className="text-slate-400">Voucher</TableHead>
          {showActions && <TableHead className="text-slate-400 text-right">Acciones</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((payment) => (
          <TableRow key={payment.id} className="border-white/15 hover:bg-slate-750" data-testid={`payment-row-${payment.id}`}>
            <TableCell>
              <div>
                <p className="font-semibold text-white text-sm sm:text-base">{payment.user?.full_name}</p>
                <p className="text-xs sm:text-sm text-slate-400 flex items-center gap-1 truncate max-w-[120px] sm:max-w-none">
                  <Mail className="w-3 h-3 flex-shrink-0" /> {payment.user?.email}
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-1 sm:hidden">
                  <Phone className="w-3 h-3" /> {payment.user?.phone || "—"}
                </p>
                {payment.record_kind === "renewal_request" && (
                  <span
                    className="mt-1 inline-block rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-bold text-violet-200 ring-1 ring-inset ring-violet-400/30"
                    title={payment.review_note}
                    data-testid={`payment-kind-renewal-${payment.id}`}
                  >
                    Renovación
                  </span>
                )}
              </div>
            </TableCell>
            <TableCell>
              <code className="text-xs text-amber-300 font-mono" data-testid={`payment-ref-${payment.id}`}>
                {payment.payment_ref || "—"}
              </code>
            </TableCell>
            <TableCell className="hidden lg:table-cell">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-300" data-testid={`payment-origin-${payment.id}`}>
                {payment.registration_source === "ANDROID" || payment.registration_source === "IOS"
                  ? <Smartphone className="w-3.5 h-3.5 text-[#5BC0FF]" aria-hidden="true" />
                  : payment.registration_source === "WEB"
                    ? <Monitor className="w-3.5 h-3.5 text-[#5BC0FF]" aria-hidden="true" />
                    : <HelpCircle className="w-3.5 h-3.5 text-slate-500" aria-hidden="true" />}
                {payment.registration_source_label || "Origen no determinado"}
              </span>
              <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`payment-client-${payment.id}`}>
                Enviado desde: {{ web: "Web", android: "App Android", ios: "App iOS", admin: "Alta del admin" }[payment.submission_client] || "Web"}
              </p>
            </TableCell>
            <TableCell className="text-slate-300 hidden sm:table-cell">
              {payment.vehicle?.name}
            </TableCell>
            <TableCell className="text-white font-semibold text-sm">
              {payment.currency === "INR" ? "₹" : "S/ "}{payment.amount}{payment.plan_months ? ` · ${payment.plan_months} meses` : ""}
              {payment.discount_value > 0 && (
                <p className="text-[10px] font-normal text-slate-400 mt-0.5" data-testid={`payment-amounts-${payment.id}`}>
                  Precio {payment.original_amount} − descuento {payment.discount_value} = <span className="text-white">{payment.final_amount}</span> {payment.currency}
                </p>
              )}
              {payment.coupon_code && (
                <div className="mt-1">
                  <Badge className="bg-[#1B84FF]/20 text-emerald-300 text-[10px]" title={`Original: ${payment.currency === "INR" ? "₹" : "S/ "}${payment.original_amount} · Descuento: ${payment.discount_value}${payment.partner_name ? ` · Partner: ${payment.partner_name}` : ""}`} data-testid={`payment-coupon-${payment.id}`}>
                    🏷 {payment.coupon_code} (antes {payment.currency === "INR" ? "₹" : "S/ "}{payment.original_amount})
                  </Badge>
                </div>
              )}
              {payment.country_mismatch && (
                <div className="mt-1">
                  <Badge className="bg-red-500/20 text-red-400 text-[10px]" title={`País de la cuenta: ${payment.account_country || "?"} · País geo-IP al pagar: ${payment.geo_country || "?"}`} data-testid={`payment-mismatch-${payment.id}`}>
                    ⚠️ país no coincide ({payment.account_country || "?"}≠{payment.geo_country || "?"})
                  </Badge>
                </div>
              )}
            </TableCell>
            <TableCell className="hidden md:table-cell">
              <Badge className={`text-xs ${payment.method === "yape" ? "bg-purple-500/20 text-purple-400" : "bg-green-500/20 text-green-400"}`} data-testid={`payment-method-badge-${payment.id}`}>
                {paymentMethodLabel(payment.method)}
              </Badge>
            </TableCell>
            <TableCell className="text-slate-300 hidden lg:table-cell">
              <div className="flex items-center gap-1 text-sm">
                <CalendarIcon className="w-3 h-3 text-slate-500" />
                {new Date(payment.created_at).toLocaleString('es-PE', {
                  dateStyle: 'short',
                  timeStyle: 'short'
                })}
              </div>
              {payment.reviewed_at && (
                <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`payment-decision-${payment.id}`}>
                  {payment.status === "approved" ? "Aprobado" : "Rechazado"} el{" "}
                  {new Date(payment.reviewed_at).toLocaleDateString('es-PE')}
                  {payment.reviewed_by_name ? ` por ${payment.reviewed_by_name}` : ""}
                </p>
              )}
              {payment.rejection_reason && (
                <p className="text-[10px] text-rose-300 mt-0.5">Motivo: {payment.rejection_reason}</p>
              )}
            </TableCell>
            <TableCell>
              {(payment.voucher_path || payment.has_voucher === true) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewVoucher(payment.id)}
                  className="text-sky-400 hover:text-sky-300 px-2"
                  data-testid={`view-voucher-${payment.id}`}
                >
                  <ZoomIn className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Ver</span>
                </Button>
              ) : (
                <span className="text-xs text-slate-500 italic" data-testid={`no-voucher-${payment.id}`}>
                  Sin voucher adjunto
                </span>
              )}
              {showActions && onAttachVoucher && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAttachVoucher(payment)}
                  className="text-[#5BC0FF] hover:text-emerald-300 px-2 ml-1"
                  data-testid={`attach-voucher-${payment.id}`}
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">{payment.voucher_path ? "Reemplazar" : "Adjuntar"}</span>
                </Button>
              )}
            </TableCell>
            {showActions && payment.record_kind === "renewal_request" ? (
              <TableCell className="text-right">
                <a
                  href={`/admin/users?user=${payment.user_id || ""}`}
                  className="inline-block rounded-md bg-white/10 px-2.5 py-1 text-xs font-semibold text-slate-200 hover:bg-white/20"
                  title={payment.review_note}
                  data-testid={`payment-goto-customer-${payment.id}`}
                >
                  Revisar en la ficha
                </a>
              </TableCell>
            ) : showActions && (
              <TableCell className="text-right">
                <Button
                  size="sm"
                  onClick={() => onReview(payment)}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm"
                  data-testid={`review-payment-${payment.id}`}
                >
                  Revisar
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const AdminPayments = () => {
  useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // All payments loaded once - single source of truth
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "pending");
  
  // Date filters
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // Search filter (iter64 — accent+case insensitive with debounce)
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebounce(searchQuery, 300);

  // MASTER ADMIN V2 §13 — filtros operativos de la cola única
  const [clientFilter, setClientFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  // iter64 — hide test data toggle
  const [hideTestData, setHideTestData] = useState(() => {
    try { return localStorage.getItem("admin_hide_test") !== "false"; } catch (_e) { return true; }
  });
  
  // (iter68 F3) counts are ALWAYS derived from the filtered lists below
  
  // Review dialog
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [reviewing, setReviewing] = useState(false);
  
  // Voucher dialog
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState(null);

  // Fetch all payments once
  useEffect(() => {
    fetchAllPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when date filters change
  useEffect(() => {
    fetchAllPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const fetchAllPayments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) {
        params.date_from = format(dateFrom, "yyyy-MM-dd");
      }
      if (dateTo) {
        params.date_to = format(dateTo, "yyyy-MM-dd") + "T23:59:59";
      }
      const response = await axios.get(`${API}/admin/payments`, { params });
      setAllPayments(response.data);
    } catch (error) {
      toast.error("Error al cargar pagos");
    } finally {
      setLoading(false);
    }
  };

  // iter68 F3 — COHERENCIA: every count derives from the SAME filtered list
  // it summarizes (hide_test + search + dates). No separate backend counts.
  // MASTER ADMIN V2 §12 — la clasificación autoritativa la calcula el backend.
  const isTestUser = (p) => {
    if (typeof p.is_qa_record === "boolean") return p.is_qa_record;
    const email = normalizeText(p.user?.email || "");
    const name = normalizeText(p.user?.full_name || "");
    return p.is_test === true || email.startsWith("test_") || email.includes("+test") ||
      email.endsWith("@test.com") || name.startsWith("test_");
  };

  const getFilteredPayments = (status) => {
    let filtered = allPayments.filter(p => p.status === status);
    if (hideTestData) filtered = filtered.filter(p => !isTestUser(p));
    if (clientFilter) filtered = filtered.filter(p => (p.submission_client || "web") === clientFilter);
    if (methodFilter) filtered = filtered.filter(p => p.method === methodFilter);
    if (sourceFilter) filtered = filtered.filter(p => (p.registration_source || "UNKNOWN_LEGACY") === sourceFilter);
    if (debouncedQuery.trim()) {
      filtered = filtered.filter(p =>
        containsNorm(p.user?.full_name, debouncedQuery) ||
        containsNorm(p.user?.email, debouncedQuery) ||
        containsNorm(p.user?.phone, debouncedQuery) ||
        containsNorm(p.vehicle?.name, debouncedQuery) ||
        containsNorm(p.vehicle?.plate_number, debouncedQuery) ||
        containsNorm(p.payment_ref, debouncedQuery)
      );
    }
    return filtered;
  };

  // Update data after any change
  const refreshData = async () => {
    await fetchAllPayments();
  };

  const handleAttachVoucher = (payment) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpg,.jpeg,.png,.webp,.pdf";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("voucher", file);
      try {
        await axios.post(`${API}/admin/uc/payments/${payment.id}/voucher`, fd);
        toast.success(`Voucher adjuntado (ref ${payment.payment_ref || "—"})`);
        fetchAllPayments();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error al adjuntar voucher");
      }
    };
    input.click();
  };

  const handleViewVoucher = async (paymentId) => {
    // iter64 — check first that payment actually has a voucher; avoid the confusing
    // "Error al cargar voucher" toast when the row has none.
    const p = allPayments.find((x) => x.id === paymentId);
    if (p && (p.has_voucher === false || (!p.voucher_path && p.has_voucher !== true))) {
      toast.info("Este pago no tiene un voucher adjunto");
      return;
    }
    try {
      const endpoint = p?.voucher_url || `${API}/admin/payments/${paymentId}/voucher`;
      const response = await axios.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      setVoucherUrl(url);
      setVoucherDialogOpen(true);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info("Este pago no tiene un voucher adjunto");
      } else {
        toast.error("Error al cargar voucher");
      }
    }
  };

  const handleReview = (payment) => {
    setSelectedPayment(payment);
    setAdminNotes("");
    setRejectionReason("");
    setReviewDialogOpen(true);
  };

  const submitReview = async (status) => {
    if (status === "rejected" && !rejectionReason.trim()) {
      toast.error("Por favor ingresa una razón de rechazo");
      return;
    }
    
    setReviewing(true);
    try {
      await axios.put(`${API}/admin/payments/${selectedPayment.id}/review`, {
        status,
        admin_notes: adminNotes || null,
        rejection_reason: status === "rejected" ? rejectionReason : null
      });
      toast.success(`Pago ${status === "approved" ? "aprobado" : "rechazado"}`);
      setReviewDialogOpen(false);
      refreshData();
    } catch (error) {
      toast.error("Error al procesar revisión");
    } finally {
      setReviewing(false);
    }
  };

  const clearDateFilters = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const clearAllFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setSearchQuery("");
    setClientFilter("");
    setMethodFilter("");
    setSourceFilter("");
  };

  // Get current tab's payments
  const pendingPayments = getFilteredPayments("pending");
  const approvedPayments = getFilteredPayments("approved");
  const rejectedPayments = getFilteredPayments("rejected");

  return (
    <AdminShell
      testid="admin-payments"
      title="Cola de pagos"
      description="Cola ÚNICA de comprobantes: la web y la app nativa entran en esta misma lista. Revisa, aprueba o rechaza con el importe real acordado."
    >
      <div>
          {/* Search and Date Filters */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardContent className="p-4 space-y-4">
              {/* Search User */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <span className="text-sm text-slate-400 whitespace-nowrap">Buscar usuario:</span>
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Nombre, email, teléfono, vehículo o placa..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    data-testid="payment-search-input"
                  />
                </div>
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchQuery("")}
                    className="text-slate-400 hover:text-white"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-300 bg-[#0B2545] border border-white/15 rounded-md px-3 h-10 cursor-pointer ml-auto" data-testid="payments-hide-test-toggle">
                  <input
                    type="checkbox"
                    checked={hideTestData}
                    onChange={(e) => {
                      setHideTestData(e.target.checked);
                      try { localStorage.setItem("admin_hide_test", String(e.target.checked)); } catch (_e) { /* ignore */ }
                    }}
                    className="accent-emerald-500"
                  />
                  Ocultar datos de prueba
                </label>
              </div>

              {/* Origen y plataforma de envío */}
              <div className="flex flex-wrap gap-3 items-center">
                <span className="text-sm text-slate-400 whitespace-nowrap">Origen y método:</span>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  aria-label="Plataforma desde la que se envió el comprobante"
                  className="h-10 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-200"
                  data-testid="payments-client-filter"
                >
                  <option value="">Enviado desde: todas</option>
                  <option value="web">Web</option>
                  <option value="android">App Android</option>
                  <option value="ios">App iOS</option>
                  <option value="admin">Alta del admin</option>
                </select>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  aria-label="Origen de registro del cliente"
                  className="h-10 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-200"
                  data-testid="payments-source-filter"
                >
                  <option value="">Origen del cliente: todos</option>
                  <option value="WEB">Web</option>
                  <option value="ANDROID">App Android</option>
                  <option value="IOS">App iOS</option>
                  <option value="UNKNOWN_LEGACY">Origen no determinado</option>
                </select>
                <select
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  aria-label="Método de pago"
                  className="h-10 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-200"
                  data-testid="payments-method-filter"
                >
                  <option value="">Método: todos</option>
                  {[...new Set(allPayments.map((p) => p.method).filter(Boolean))].map((m) => (
                    <option key={m} value={m}>{paymentMethodLabel(m)}</option>
                  ))}
                </select>
              </div>

              {/* Date Filters */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <span className="text-sm text-slate-400 whitespace-nowrap">Filtrar por fecha:</span>
                
                {/* Date From */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full sm:w-44 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                      data-testid="payment-date-from-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, "dd/MM/yyyy") : "Desde"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#14375F] border-white/15" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      locale={es}
                      className="bg-[#14375F] text-white"
                    />
                  </PopoverContent>
                </Popover>
                
                {/* Date To */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full sm:w-44 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                      data-testid="payment-date-to-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, "dd/MM/yyyy") : "Hasta"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#14375F] border-white/15" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      locale={es}
                      className="bg-[#14375F] text-white"
                    />
                  </PopoverContent>
                </Popover>
                
                {/* Clear Dates */}
                {(dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDateFilters}
                    className="text-slate-400 hover:text-white"
                    data-testid="clear-payment-dates-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpiar fechas
                  </Button>
                )}
              </div>

              {/* Clear All Filters */}
              {(searchQuery || dateFrom || dateTo || clientFilter || methodFilter || sourceFilter) && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                    data-testid="clear-all-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpiar todos los filtros
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabs — counts derive from the SAME filtered lists they summarize (iter68 F3) */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 flex w-full max-w-full flex-wrap justify-start gap-1 border border-white/15 bg-[#14375F] sm:w-auto sm:flex-nowrap">
              <TabsTrigger 
                value="pending" 
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-white"
                data-testid="tab-pending"
              >
                <Clock className="w-4 h-4 mr-2" />
                Pendientes ({pendingPayments.length})
              </TabsTrigger>
              <TabsTrigger 
                value="approved" 
                className="data-[state=active]:bg-[#1B84FF] data-[state=active]:text-white"
                data-testid="tab-approved"
              >
                <Check className="w-4 h-4 mr-2" />
                Aprobados ({approvedPayments.length})
              </TabsTrigger>
              <TabsTrigger 
                value="rejected" 
                className="data-[state=active]:bg-red-500 data-[state=active]:text-white"
                data-testid="tab-rejected"
              >
                <X className="w-4 h-4 mr-2" />
                Rechazados ({rejectedPayments.length})
              </TabsTrigger>
            </TabsList>

            <Card className="bg-[#14375F] border-white/15">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                  </div>
                ) : (
                  <>
                    <TabsContent value="pending" className="mt-0">
                      {pendingPayments.length === 0 ? (
                        <div className="text-center py-12">
                          <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                          <p className="text-slate-400">No hay pagos pendientes</p>
                        </div>
                      ) : (
                        <PaymentTable data={pendingPayments} showActions onViewVoucher={handleViewVoucher} onReview={handleReview} onAttachVoucher={handleAttachVoucher} />
                      )}
                    </TabsContent>

                    <TabsContent value="approved" className="mt-0">
                      {approvedPayments.length === 0 ? (
                        <div className="text-center py-12">
                          <Check className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                          <p className="text-slate-400">No hay pagos aprobados</p>
                        </div>
                      ) : (
                        <PaymentTable data={approvedPayments} onViewVoucher={handleViewVoucher} onReview={handleReview} />
                      )}
                    </TabsContent>

                    <TabsContent value="rejected" className="mt-0">
                      {rejectedPayments.length === 0 ? (
                        <div className="text-center py-12">
                          <X className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                          <p className="text-slate-400">No hay pagos rechazados</p>
                        </div>
                      ) : (
                        <PaymentTable data={rejectedPayments} onViewVoucher={handleViewVoucher} onReview={handleReview} />
                      )}
                    </TabsContent>
                  </>
                )}
              </CardContent>
            </Card>
          </Tabs>

          {/* Results Count */}
          {!loading && (
            <p className="text-sm text-slate-500 mt-4 text-right">
              Mostrando: {pendingPayments.length + approvedPayments.length + rejectedPayments.length} de {allPayments.filter(p => !hideTestData || !isTestUser(p)).length} pago(s) 
              {(searchQuery || dateFrom || dateTo) && " (filtrado)"}
            </p>
          )}
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Revisar Pago</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedPayment?.user?.full_name} - {selectedPayment?.vehicle?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Payment Details */}
            <div className="bg-slate-700 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Monto</p>
                  <p className="text-white font-semibold">S/ {selectedPayment?.amount}</p>
                  {selectedPayment?.coupon_code && (
                    <p className="text-emerald-300 text-xs mt-0.5" data-testid="payment-detail-coupon">
                      🏷 Cupón {selectedPayment.coupon_code} · original <span className="line-through">{selectedPayment.currency === "INR" ? "₹" : "S/ "}{selectedPayment.original_amount}</span> · desc. {selectedPayment.discount_value}
                      {selectedPayment.partner_name ? ` · ${selectedPayment.partner_name}` : ""}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-slate-400">Método</p>
                  <p className="text-white font-semibold capitalize">{selectedPayment?.method}</p>
                </div>
                <div>
                  <p className="text-slate-400">Email</p>
                  <p className="text-white">{selectedPayment?.user?.email}</p>
                </div>
                <div>
                  <p className="text-slate-400">Teléfono</p>
                  <p className="text-white">{selectedPayment?.user?.phone || "—"}</p>
                </div>
              </div>
            </div>

            {/* View Voucher Button */}
            <Button
              variant="outline"
              onClick={() => handleViewVoucher(selectedPayment?.id)}
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ZoomIn className="w-4 h-4 mr-2" />
              Ver Voucher Completo
            </Button>
            
            {/* Admin Notes */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Notas internas (opcional)</label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notas para registro interno..."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="admin-notes-input"
              />
            </div>

            {/* Rejection Reason */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Razón de rechazo (requerido si rechaza)</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Ej: Voucher borroso, monto incorrecto, nombre no coincide..."
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="rejection-reason-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => submitReview("rejected")}
              disabled={reviewing}
              className="border-red-500 text-red-400 hover:bg-red-500/20"
              data-testid="reject-payment-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Rechazar
            </Button>
            <Button
              onClick={() => submitReview("approved")}
              disabled={reviewing}
              className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-white"
              data-testid="approve-payment-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher Dialog */}
      <Dialog open={voucherDialogOpen} onOpenChange={setVoucherDialogOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-white">Comprobante de Pago</DialogTitle>
          </DialogHeader>
          {voucherUrl && (
            <div className="flex justify-center overflow-auto max-h-[70vh]">
              <img src={voucherUrl} alt="Voucher" className="max-w-full rounded-lg" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoucherDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
};

export default AdminPayments;
